import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";

const calculateTotalScore = (scores) =>
  scores?.reduce((t, s) => t + Number(s.score || 0), 0) || 0;

const assignRanks = (students) => {
  if (!students?.length) return [];
  const sorted = [...students]
    .map((s) => ({ ...s, totalScore: calculateTotalScore(s.scores) }))
    .sort((a, b) => b.totalScore - a.totalScore);

  let last = null;
  let rank = 1;

  return sorted.map((s, i) => {
    if (s.totalScore !== last) {
      last = s.totalScore;
      rank = i + 1;
    }
    return { ...s, classRank: rank };
  });
};

const ClassTopperPage = () => {
  const currentSessionId = localStorage.getItem("currentSessionId");

  const [classes, setClasses] = useState([]);
  const [sections, setSections] = useState([]);
  const [allStudents, setAllStudents] = useState([]);
  const [loading, setLoading] = useState(false);

  const [filterClass, setFilterClass] = useState("");
  const [filterSection, setFilterSection] = useState("");

  // Colors and crown for ranks
  const rankColor = (r) =>
    r === 1
      ? "bg-yellow-400 text-black"
      : r === 2
      ? "bg-gray-300 text-black"
      : r === 3
      ? "bg-amber-700 text-white"
      : "bg-blue-600 text-white";

  const crown = (r) =>
    r === 1 ? "👑" : r === 2 ? "🥈" : r === 3 ? "🥉" : "🎓";

  // ===================== FETCH DATA =====================

  // Fetch classes and students for session
  useEffect(() => {
    if (!currentSessionId) return;

    const fetchData = async () => {
      setLoading(true);
      try {
        const [classesRes, studentsRes] = await Promise.all([
          axios.get(`${process.env.REACT_APP_API_BASE_URL}/api/classes/getClasses`, {
            params: { sessionId: currentSessionId },
          }),
          axios.get(
            `${process.env.REACT_APP_API_BASE_URL}/api/examresults/getAllExamResults`,
            { params: { sessionId: currentSessionId } }
          ),
        ]);

        setClasses(classesRes.data);

        // Flatten students from all classes/sections
        const students = studentsRes.data.flatMap((cls) =>
          cls.students?.map((s) => ({
            ...s,
            className: cls.className,
            sectionName: cls.sectionName,
          })) || []
        );

        setAllStudents(students);
      } catch (err) {
        console.error("Error loading data:", err.response?.data || err.message);
        setClasses([]);
        setAllStudents([]);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [currentSessionId]);

  // Fetch sections dynamically when class changes
  useEffect(() => {
    if (!filterClass || !currentSessionId) {
      setSections([]);
      setFilterSection("");
      return;
    }

    const fetchSections = async () => {
      try {
        const res = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/sections/${encodeURIComponent(
            filterClass
          )}/getSectionsByClassName`,
          { params: { sessionId: currentSessionId } }
        );
        setSections(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error("Error fetching sections:", err.response?.data || err.message);
        setSections([]);
      }
    };

    fetchSections();
  }, [filterClass, currentSessionId]);

  // ===================== FILTER & RANK =====================

  const filteredStudents = useMemo(
    () =>
      allStudents.filter(
        (s) =>
          (!filterClass || s.className === filterClass) &&
          (!filterSection || s.sectionName === filterSection)
      ),
    [allStudents, filterClass, filterSection]
  );

  const rankedFilteredStudents = useMemo(
    () => assignRanks(filteredStudents),
    [filteredStudents]
  );

  const overallToppers = useMemo(
    () => assignRanks(allStudents).slice(0, 6),
    [allStudents]
  );

  // ===================== RENDER =====================

  if (loading)
    return (
      <p className="text-center py-8 text-lg font-semibold text-blue-600">
        Loading...
      </p>
    );

  return (
    <div className="w-full px-6 py-6 max-w-6xl mx-auto">
      <h1 className="text-3xl font-bold text-center text-blue-700 mb-6">
        🏆 Class Toppers
      </h1>

      {/* Overall Toppers */}
      <h2 className="text-xl font-semibold mb-3">🏅 Overall Top Toppers</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-10">
        {overallToppers.map((s) => (
          <div
            key={s._id || s.rollNo + s.studentName}
            className="p-3 rounded-lg border bg-white shadow-sm flex items-center gap-3"
          >
            <div
              className={`px-3 py-1 text-xs font-semibold rounded-full ${rankColor(
                s.classRank
              )}`}
            >
              {crown(s.classRank)} #{s.classRank}
            </div>
            <div>
              <p className="font-bold text-gray-800">{s.studentName}</p>
              <p className="text-sm text-gray-500">Class: {s.className}</p>
              <p className="text-sm font-semibold text-blue-700">
                Score: {s.totalScore}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="bg-white p-4 rounded-lg shadow mb-6 flex flex-wrap gap-4 items-end">
        <div>
          <label className="block text-sm font-semibold mb-1">Class</label>
          <select
            className="border px-3 py-2 rounded w-32"
            value={filterClass}
            onChange={(e) => {
              setFilterClass(e.target.value);
              setFilterSection("");
            }}
          >
            <option value="">All</option>
            {classes.map((c) => (
              <option key={c._id} value={c.className}>
                {c.className}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-semibold mb-1">Section</label>
          <select
            disabled={!filterClass}
            className="border px-3 py-2 rounded w-32 bg-white disabled:bg-gray-100 disabled:text-gray-400"
            value={filterSection}
            onChange={(e) => setFilterSection(e.target.value)}
          >
            <option value="">All</option>
            {sections.map((sec) => (
              <option key={sec._id || sec.sectionName} value={sec.sectionName}>
                {sec.sectionName}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Filtered Toppers */}
      <h2 className="text-xl font-semibold mb-3">📋 List of Filtered Toppers</h2>
      <div className="border rounded-lg bg-white shadow-sm">
        <div className="grid grid-cols-5 font-semibold bg-gray-100 p-3">
          <span>Rank</span>
          <span>Name</span>
          <span>Class</span>
          <span>Section</span>
          <span className="text-right">Score</span>
        </div>

        {rankedFilteredStudents.length ? (
          rankedFilteredStudents.map((s, i) => (
            <div
              key={s._id || s.rollNo + s.studentName}
              className="grid grid-cols-5 p-3 border-t hover:bg-gray-50 text-sm"
            >
              <span className="font-semibold">{crown(s.classRank)}</span>
              <span>{s.studentName}</span>
              <span>{s.className}</span>
              <span>{s.sectionName}</span>
              <span className="text-right font-semibold">{s.totalScore}</span>
            </div>
          ))
        ) : (
          <div className="p-3 text-gray-500 text-center">No students found for selected filters.</div>
        )}
      </div>
    </div>
  );
};

export default ClassTopperPage;
